import { useEffect, useRef, MutableRefObject } from 'react';
import { Stack } from '@mui/material';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';

import { useChatState, useChat } from 'hooks';
import { ChatUiMessage } from 'types/chat';
import { getIntlLocale } from 'utils';

import { MessageInfo } from '../MessageInfo';
import { ChatMessage } from '../ChatMessage';

import styles from './MessagesList.module.scss';

type MessagesListProps = {
  isGroup: boolean;
  className?: string;
  forceLightTheme?: boolean;
};

export function MessagesList({ isGroup, className, forceLightTheme }: MessagesListProps): JSX.Element {
  const { t, i18n } = useTranslation('providerRoom');
  const { messages, participants } = useChatState(isGroup);
  const { identity } = useChat();
  const youLabel = t('chatYou');
  const locale = getIntlLocale(i18n.resolvedLanguage || i18n.language || 'en');

  const scrollRef: MutableRefObject<HTMLDivElement | null> = useRef<HTMLDivElement | null>(null);

  const getFormatedTime = (date: Date | undefined | null): string => {
    return date
      ? new Intl.DateTimeFormat(locale, {
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        }).format(date)
      : '';
  };

  useEffect(() => {
    if (messages) {
      scrollRef.current!.scrollTop = scrollRef.current!.scrollHeight;
    }
  }, [messages]);

  return (
    <Stack
      className={classnames(styles.messages, className, { [styles.forceLight]: forceLightTheme })}
      direction='column'
      alignItems='flex-start'
      spacing='10px'
      ref={scrollRef}
    >
      {messages.map((message: ChatUiMessage, index: number) => {
        const time: string = getFormatedTime(message.dateCreated!);
        const previousTime: string = getFormatedTime(messages[index - 1]?.dateCreated);

        const shouldDisplayMessageInfo: boolean =
          time !== previousTime || message.author !== messages[index - 1]?.author;

        const participantLabel = participants.get(message.author ?? '') || '';
        const isMyMessage: boolean = message.author === identity || participantLabel === youLabel;
        const author: string = isGroup ? (isMyMessage ? youLabel : participantLabel || message.author || '') : '';

        return (
          <div key={index} className={isMyMessage ? styles.messageContainerMy : styles.messageContainerOther}>
            {shouldDisplayMessageInfo && (
              <MessageInfo
                dateCreated={message.dateCreated!}
                isMy={isMyMessage}
                author={author}
                forceLightTheme={forceLightTheme}
              />
            )}
            <ChatMessage message={message} isMy={isMyMessage} isGroup={isGroup} />
          </div>
        );
      })}
    </Stack>
  );
}
